import React, { useEffect, useState } from 'react';
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaGithub,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
  FaTrash,
  FaEnvelopeOpen,
  FaEdit,
  FaTimes,
  FaCheck,
  FaSync,
  FaSearch
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4200';

const ManageContact = () => {
  const [contactDetails, setContactDetails] = useState({
    email: "",
    contact: "",
    address: "",
    openHour: "",
    socialMedia: { facebook: "", instagram: "", linkedin: "", github: "" }
  });

  const [contactMessages, setContactMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);

  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ total: 0, unread: 0, read: 0, last30Days: 0 });

  const fetchContactDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/contact-details`, { withCredentials: true });
      if (response.data.success) setContactDetails(response.data.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load contact details');
    }
  };

  const fetchContactMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/contacts`, { withCredentials: true });
      if (response.data.success) {
        setContactMessages(response.data.data);
        setFilteredMessages(response.data.data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/contacts/stats`, { withCredentials: true });
      if (response.data.success) setStats(response.data.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    fetchContactDetails();
    fetchContactMessages();
    fetchStats();
  }, []);

  useEffect(() => {
    let filtered = [...contactMessages];
    if (filterStatus === 'read') filtered = filtered.filter(msg => msg.read);
    else if (filterStatus === 'unread') filtered = filtered.filter(msg => !msg.read);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(msg =>
        msg.name.toLowerCase().includes(query) ||
        msg.email.toLowerCase().includes(query) ||
        msg.subject.toLowerCase().includes(query)
      );
    }
    setFilteredMessages(filtered);
  }, [filterStatus, searchQuery, contactMessages]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (['facebook', 'instagram', 'linkedin', 'github'].includes(name)) {
      setContactDetails(prev => ({
        ...prev,
        socialMedia: { ...prev.socialMedia, [name]: value }
      }));
    } else {
      setContactDetails(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdateDetails = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.put(`${API_URL}/admin/contact-details`, contactDetails, { withCredentials: true });
      if (response.data.success) {
        toast.success('Contact details updated successfully');
        setShowDetailsModal(false);
        fetchContactDetails();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/admin/contacts/${id}/read`, {}, { withCredentials: true });
      fetchContactMessages();
      fetchStats();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API_URL}/admin/contacts/${id}`, { withCredentials: true });
      toast.success('Message deleted');
      fetchContactMessages();
      fetchStats();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete');
    }
  };

  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
    if (!message.read) handleMarkAsRead(message._id);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8 font-sans text-slate-900">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Messages & Inquiries</h1>
          <p className="text-slate-500 mt-1">Manage contact details and view incoming messages</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { fetchContactDetails(); fetchContactMessages(); fetchStats(); }}
            className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-indigo-600 transition-all font-semibold flex items-center gap-2 shadow-sm"
          >
            <FaSync size={14} /> Refresh
          </button>
          <button
            onClick={() => setShowDetailsModal(true)}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all transform hover:scale-105"
          >
            <FaEdit size={16} /> Edit Details
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {[
          { label: 'Total Messages', value: stats.total, icon: FaEnvelope, color: 'text-blue-500', bg: 'bg-blue-50' },
          { label: 'Unread', value: stats.unread, icon: FaEnvelopeOpen, color: 'text-amber-500', bg: 'bg-amber-50' },
          { label: 'Read', value: stats.read, icon: FaCheck, color: 'text-emerald-500', bg: 'bg-emerald-50' },
          { label: 'Recent (30d)', value: stats.last30Days, icon: FaClock, color: 'text-violet-500', bg: 'bg-violet-50' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center justify-between group hover:shadow-md transition-all">
            <div>
              <p className="text-slate-500 text-sm font-semibold mb-1">{stat.label}</p>
              <h3 className="text-3xl font-black text-slate-800">{stat.value}</h3>
            </div>
            <div className={`w-12 h-12 rounded-2xl ${stat.bg} ${stat.color} flex items-center justify-center text-xl group-hover:scale-110 transition-transform`}>
              <stat.icon />
            </div>
          </div>
        ))}
      </div>

      {/* Contact Info Preview */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-8">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <FaMapMarkerAlt className="text-indigo-500" /> Public Contact Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
            <label className="text-xs font-bold text-slate-400 uppercase">Email</label>
            <p className="text-slate-800 font-semibold truncate" title={contactDetails.email}>{contactDetails.email || "Not set"}</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
            <label className="text-xs font-bold text-slate-400 uppercase">Phone</label>
            <p className="text-slate-800 font-semibold truncate">{contactDetails.contact || "Not set"}</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
            <label className="text-xs font-bold text-slate-400 uppercase">Address</label>
            <p className="text-slate-800 font-semibold truncate" title={contactDetails.address}>{contactDetails.address || "Not set"}</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
            <label className="text-xs font-bold text-slate-400 uppercase">Hours</label>
            <p className="text-slate-800 font-semibold truncate">{contactDetails.openHour || "Not set"}</p>
          </div>
        </div>

        <div className="mt-6 flex gap-3 flex-wrap">
          {Object.entries(contactDetails.socialMedia).map(([key, url]) => (
            url && (
              <a key={key} href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm font-semibold hover:bg-slate-200 transition-colors capitalize">
                {key === 'facebook' && <FaFacebook />}
                {key === 'instagram' && <FaInstagram />}
                {key === 'linkedin' && <FaLinkedin />}
                {key === 'github' && <FaGithub />}
                {key}
              </a>
            )
          ))}
        </div>
      </div>

      {/* Messages Table */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">

        {/* Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
          <h2 className="font-bold text-xl text-slate-800">Inbox</h2>
          <div className="flex gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search inbox..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm font-semibold text-slate-600"
            >
              <option value="all">All Status</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading messages...</div>
        ) : filteredMessages.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 mb-4">
              <FaEnvelopeOpen size={32} />
            </div>
            <p className="text-slate-500 font-medium">No messages found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="p-6">Sender</th>
                  <th className="p-6">Subject</th>
                  <th className="p-6">Date</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMessages.map(msg => (
                  <tr
                    key={msg._id}
                    onClick={() => openMessageModal(msg)}
                    className={`hover:bg-slate-50 transition-colors cursor-pointer group ${!msg.read ? 'bg-indigo-50/30' : ''}`}
                  >
                    <td className="p-6">
                      <div className="font-bold text-slate-800">{msg.name}</div>
                      <div className="text-xs text-slate-500">{msg.email}</div>
                    </td>
                    <td className="p-6">
                      <div className="text-slate-700 font-medium truncate max-w-xs">{msg.subject}</div>
                    </td>
                    <td className="p-6">
                      <div className="text-sm text-slate-500">{new Date(msg.createdAt).toLocaleDateString()}</div>
                      <div className="text-xs text-slate-400">{new Date(msg.createdAt).toLocaleTimeString()}</div>
                    </td>
                    <td className="p-6">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${msg.read
                          ? "bg-slate-100 text-slate-500"
                          : "bg-amber-100 text-amber-700"
                        }`}>
                        {msg.read ? (
                          <><FaCheck size={10} /> Read</>
                        ) : (
                          <><FaEnvelope size={10} /> Unread</>
                        )}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg._id); }}
                        className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <FaTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* EDIT DETAILS MODAL */}
      {showDetailsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-bold text-xl text-slate-800">Edit Company Information</h3>
              <button onClick={() => setShowDetailsModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleUpdateDetails} className="p-8 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email</label>
                  <input type="email" name="email" value={contactDetails.email} onChange={handleInputChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-slate-700" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Phone</label>
                  <input type="text" name="contact" value={contactDetails.contact} onChange={handleInputChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-slate-700" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Address</label>
                  <textarea name="address" value={contactDetails.address} onChange={handleInputChange} rows="2" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-slate-700 resize-none"></textarea>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Open Hours</label>
                  <input type="text" name="openHour" value={contactDetails.openHour} onChange={handleInputChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold text-slate-700" placeholder="e.g. Mon-Fri: 9am - 6pm" />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-6">
                <h4 className="font-bold text-slate-800 mb-4">Social Media Links</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['facebook', 'instagram', 'linkedin', 'github'].map(key => (
                    <div key={key}>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{key}</label>
                      <input type="url" name={key} value={contactDetails.socialMedia[key]} onChange={handleInputChange} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm text-slate-700" placeholder="https://..." />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowDetailsModal(false)} className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-colors">Cancel</button>
                <button type="submit" disabled={loading} className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MESSAGE VIEW MODAL */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl relative animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="font-bold text-2xl text-slate-800">{selectedMessage.name}</h3>
                <p className="text-slate-500 text-sm">{selectedMessage.email}</p>
              </div>
              <button onClick={() => setShowMessageModal(false)} className="text-slate-400 hover:text-slate-600 bg-slate-50 p-2 rounded-full hover:bg-slate-100 transition-colors">
                <FaTimes size={18} />
              </button>
            </div>

            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold text-indigo-500 uppercase tracking-wide">Subject</span>
                <span className="text-xs text-slate-400">•</span>
                <span className="text-xs text-slate-400">{new Date(selectedMessage.createdAt).toLocaleString()}</span>
              </div>
              <h4 className="font-bold text-slate-800 mb-2">{selectedMessage.subject}</h4>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{selectedMessage.message}</p>
            </div>

            <div className="flex justify-between items-center">
              <div className={`px-3 py-1.5 rounded-full text-xs font-bold ${selectedMessage.read ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {selectedMessage.read ? "Status: Read" : "Status: Unread"}
              </div>
              <div className="flex gap-3">
                <button onClick={() => { handleDeleteMessage(selectedMessage._id); setShowMessageModal(false); }} className="px-4 py-2 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl font-bold transition-colors flex items-center gap-2">
                  <FaTrash size={14} /> Delete
                </button>
                <button onClick={() => setShowMessageModal(false)} className="px-5 py-2 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ManageContact;